import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { orderBy } from "lodash";

import { Alert, SelectChangeEvent, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FilterList, ImportExport, RestartAlt } from "@mui/icons-material";


import { MindsetListSkeleton } from "./MindsetListSkeleton";
import {
  ButtonIcon,
  FilterBox,
  MindsetCard,
  SearchBox,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { useAudienceDispatch } from "@/context";
import { useAudiences } from "@/hooks";
import { FilterOption, MindsetAudience } from "@/types";
import { audienceSortOptions, filterAudiences } from "@/utils";


const MindsetSegmentListPage: React.FC = () => {
  const dispatch = useAudienceDispatch();
  const navigate = useNavigate();

  const { audiences, loading, error } = useAudiences("mindset");

  const [rows, setRows] = useState<MindsetAudience[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [filterBy, setFilterBy] = useState<string>("");
  const [projects, setProjects] = useState<FilterOption[]>([]);

  useEffect(() => {
    if (!audiences) return;

    // Explode the segments into individual audiences
    const segments = orderBy(audiences.flatMap((audience) => {

      const { segments, audience_size, ...other } = audience;

      return segments.map((segment, index) => {
        const size = audience_size && audience_size[index] ? audience_size[index] : undefined;
        return {
          ...other,
          audience_id: segment.segment_id.toString(),
          audience_name: segment.segment_name,
          audience_size: size,
          project_name: other.audience_name,
          segments: [],
        } as MindsetAudience
      })
    }), 'audience_name');

    // Extract the mindsets as projects - for filtering
    setProjects(Array.from(
      new Set(
        segments
          ?.filter((a) => a.project_name !== null)
          .map((a) => a.project_name as string)
          .sort((a, b) => a.localeCompare(b))
      )
    ).map((x) => ({ name: x, label: x })))

    // Filter the segments by project name and search text
    const filteredSegments = filterAudiences(
      segments,
      searchText,
      filterBy,
      sortBy
    ) as MindsetAudience[] | undefined;

    filteredSegments && setRows(filteredSegments);
  }, [audiences, searchText, sortBy, filterBy]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSortSelect = (event: SelectChangeEvent<string | string[]>) =>
    setSortBy(event.target.value as string);

  const handleFilterBySelect = (value: string) =>
    setFilterBy((prevState) => (prevState === value ? "" : value));

  const handleReset = () => {
    setSearchText("");
    setFilterBy("");
    setSortBy("");
  };

  const handleAudienceSelect = (audience: MindsetAudience) => {
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    navigate(`${audience.audience_id}`);
  };

  return (
    <PageContainer>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>Audiences</Typography>
        </Grid>
        <Grid container size={12} alignItems="center">
          <Grid>
            <SearchBox value={searchText} onSearch={handleSearch} />
          </Grid>
          <Grid>
            <FilterBox
              label="Filter by Audience Group"
              value={filterBy}
              options={projects}
              onClick={handleFilterBySelect}
              startIcon={<FilterList fontSize="small" />}
              showSelected
              width={280}
            />
          </Grid>
          {/*<Grid>*/}
          {/*  <FilterBox*/}
          {/*    label="Sort"*/}
          {/*    value={sortBy}*/}
          {/*    options={audienceSortOptions}*/}
          {/*    onChange={handleSortSelect}*/}
          {/*    startIcon={<ImportExport fontSize="small" />}*/}
          {/*    showSelected*/}
          {/*  />*/}
          {/*</Grid>*/}
          <Grid>
            <ButtonIcon
              onClick={handleReset}
              color="info"
              size={38}
              Icon={RestartAlt}
              tooltip="Reset Filters"
            />
          </Grid>
        </Grid>

        <Grid container size={12} mb={4}>
          {loading ? (
            <MindsetListSkeleton />
          ) : error ? (
            <Grid>
              <Alert variant="filled" severity="error">
                It was not possible to load your audiences at this time, please
                try again.
              </Alert>
            </Grid>
          ) : (
            rows.map((audience, index) => (
              <Grid key={index}>
                <MindsetCard
                  mindset={audience}
                  onSelect={handleAudienceSelect}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default MindsetSegmentListPage;
